import React from 'react';
import { motion } from 'framer-motion';

const About = () => {
  return (
    <div className="min-h-screen bg-[#F8F6F2]">
      {/* Hero Section */}
      <section className="relative h-screen">
        <div className="absolute inset-0">
          <img
            src="/images/factory.jpg"
            alt="Factory background"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-black/40" />
        </div>
        <div className="container mx-auto px-4 h-full flex flex-col justify-center relative z-10">
          <motion.h1 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
            className="font-playfair text-5xl md:text-7xl font-light text-white leading-tight"
          >
            Crafting Timeless <br/> Pieces with Purpose
          </motion.h1>
          <motion.p 
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mt-6 text-lg text-white/90 max-w-xl font-lora"
          >
            A SEDEX-approved manufacturer blending traditional techniques with contemporary design to create functional, timeless, and beautiful pieces.
          </motion.p>
        </div>
      </section>

      {/* About Us Section */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-16 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <h2 className="font-playfair text-4xl md:text-5xl font-light text-[#2C231E] mt-2 mb-8">
                Our Story
              </h2>
              <div className="space-y-6 text-[#2C231E]/70 font-lora leading-relaxed">
                <p>
                  ANG is a SEDEX-approved manufacturer and exporter of handcrafted furniture, serveware, and home décor based in Bagru, Rajasthan. Rooted in India's rich artisanal legacy, we blend traditional techniques with contemporary design to craft products that are functional, timeless, and beautiful.
                </p>
                <p>
                  From accent tables and kitchen platters to mirrors and storage boxes, our collections celebrate clean forms, purposeful design, and natural textures. We work primarily with solid woods like Acacia, Mango, Oak, and Pine, often paired with brass, iron, and marble to create versatile pieces for modern living.
                </p>
              </div>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
            >
              <img 
                src="/images/workshop.jpg" 
                alt="Our workshop" 
                className="rounded-lg shadow-xl w-full h-auto object-cover aspect-square"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* What We Do Section */}
      <section className="py-24 bg-[#F1F5E9]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-[#2C231E] font-lora font-light text-sm uppercase tracking-wider"
            >
              What We Do
            </motion.p>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="font-playfair text-4xl md:text-5xl font-light text-[#2C231E] mt-2"
            >
              Our Specializations
            </motion.h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <span className="material-icons text-5xl text-[#2C231E] mb-4">chair</span>
              <h3 className="font-playfair text-2xl font-light text-[#2C231E] mb-4">Handcrafted Furniture</h3>
              <p className="text-[#2C231E]/70 font-lora leading-relaxed">
                Expertly crafted wooden and metal furniture pieces that combine functionality with aesthetic appeal.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <span className="material-icons text-5xl text-[#2C231E] mb-4">kitchen</span>
              <h3 className="font-playfair text-2xl font-light text-[#2C231E] mb-4">Kitchen & Serveware</h3>
              <p className="text-[#2C231E]/70 font-lora leading-relaxed">
                Beautiful wooden kitchenware and serveware that brings elegance to every dining experience.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <span className="material-icons text-5xl text-[#2C231E] mb-4">home</span>
              <h3 className="font-playfair text-2xl font-light text-[#2C231E] mb-4">Home Décor</h3>
              <p className="text-[#2C231E]/70 font-lora leading-relaxed">
                Thoughtfully designed home décor and utility pieces that add character to any space.
              </p>
            </motion.div>
          </div>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-12 text-[#2C231E]/70 font-lora leading-relaxed max-w-3xl mx-auto"
          >
            Each piece is made with precision and care at our 3,000 sq. m. facility in Bagru, where skilled artisans and advanced machinery come together. We support low MOQs and deliver within ~60 days, making us a trusted manufacturing partner for brands globally.
          </motion.p>
        </div>
      </section>

      {/* Who We Work With Section */}
      <section className="py-24 bg-[#F1F5E9]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-[#2C231E] font-lora font-light text-sm uppercase tracking-wider"
            >
              Who We Work With
            </motion.p>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="font-playfair text-4xl md:text-5xl font-light text-[#2C231E] mt-2"
            >
              Our Partners
            </motion.h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <span className="material-icons text-5xl text-[#2C231E] mb-4">architecture</span>
              <h3 className="font-playfair text-2xl font-light text-[#2C231E] mb-4">Architects & Interior Designers</h3>
              <p className="text-[#2C231E]/70 font-lora leading-relaxed">
                We create custom furniture and decor for homes, cafes, and boutique spaces with flexible designs, finish options, and reliable execution.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <span className="material-icons text-5xl text-[#2C231E] mb-4">storefront</span>
              <h3 className="font-playfair text-2xl font-light text-[#2C231E] mb-4">Retailers & Lifestyle Brands</h3>
              <p className="text-[#2C231E]/70 font-lora leading-relaxed">
                We partner with stores and lifestyle brands to offer shelf-ready collections and co-branded furniture with low minimums and premium finish.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-lg shadow-lg text-center"
            >
              <span className="material-icons text-5xl text-[#2C231E] mb-4">language</span>
              <h3 className="font-playfair text-2xl font-light text-[#2C231E] mb-4">Buying Agencies & Export Houses</h3>
              <p className="text-[#2C231E]/70 font-lora leading-relaxed">
                From Jaipur to the world, we provide export-ready furniture in wood, metal, and marble with scalable production and global packaging standards.
              </p>
            </motion.div>
          </div>
          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-12 text-[#2C231E]/70 font-lora leading-relaxed max-w-3xl mx-auto"
          >
            Our collaborative approach ensures that each partnership is built on understanding, trust, and a shared commitment to quality. 
            Whether you're a global brand or a boutique studio, we adapt our processes to meet your specific needs.
          </motion.p>
        </div>
      </section>

      {/* Team Section */}
      <section className="py-24 bg-[#F1F5E9]">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <motion.p 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="text-[#2C231E] font-lora font-light text-sm uppercase tracking-wider"
            >
              Meet the Team
            </motion.p>
            <motion.h2 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="font-playfair text-4xl md:text-5xl font-light text-[#2C231E] mt-2"
            >
              Our Leadership
            </motion.h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-lg shadow-md text-center group hover:shadow-xl transition-shadow duration-300"
            >
              <div className="relative w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-[#E8E2DC] group-hover:border-[#2C231E] transition-colors duration-300">
                <img 
                  src="/images/team-1.jpg" 
                  alt="Portrait of Nikhil Gupta" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-playfair text-xl font-light text-[#2C231E]">Nikhil Gupta</h3>
              <p className="text-[#2C231E] font-lora font-medium mb-2">Partner</p>
              <p className="text-[#2C231E]/70 font-lora text-sm leading-relaxed">
                An MBA with a deep entrepreneurial drive, Nikhil envisioned bringing fresh energy into the handicraft industry. His goal was to build a scalable, design-forward brand rooted in craftsmanship and ethical practices.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-lg shadow-md text-center group hover:shadow-xl transition-shadow duration-300"
            >
              <div className="relative w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-[#E8E2DC] group-hover:border-[#2C231E] transition-colors duration-300">
                <img 
                  src="/images/team-2.jpg" 
                  alt="Portrait of Ashok Gupta" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-playfair text-xl font-light text-[#2C231E]">Ashok Gupta</h3>
              <p className="text-[#2C231E] font-lora font-medium mb-2">Partner</p>
              <p className="text-[#2C231E]/70 font-lora text-sm leading-relaxed">
                With over 35 years of experience in the handicraft industry, Ashok Gupta previously served as CFO at Dileep Industries. His insights and leadership continue to shape ANG's strategic vision and commitment to quality.
              </p>
            </motion.div>

            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.4 }}
              viewport={{ once: true }}
              className="bg-white p-6 rounded-lg shadow-md text-center group hover:shadow-xl transition-shadow duration-300"
            >
              <div className="relative w-32 h-32 mx-auto mb-4 rounded-full overflow-hidden border-4 border-[#E8E2DC] group-hover:border-[#2C231E] transition-colors duration-300">
                <img 
                  src="/images/team-3.jpg" 
                  alt="Portrait of Priyanka Gupta" 
                  className="w-full h-full object-cover"
                />
              </div>
              <h3 className="font-playfair text-xl font-light text-[#2C231E]">Priyanka Gupta</h3>
              <p className="text-[#2C231E] font-lora font-medium mb-2">Creative Head</p>
              <p className="text-[#2C231E]/70 font-lora text-sm leading-relaxed">
                Priyanka brings a keen eye for design and a strong foundation in UI/UX, leading the digital direction at ANG. She shapes the visual identity of our brand across all digital touchpoints — from website experiences to social content and B2C storytelling.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About; 